import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import Layout from "../../components/Layout";
import { Button } from "../../components/ui/button";
import {
  CheckCircle2,
  Loader2,
  XCircle,
  ArrowRight,
  MessageSquare,
  FileText,
} from "lucide-react";

export default function AgentBillingSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [subscription, setSubscription] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      const res = await apiClient.get(`/agent/billing/checkout/status/${sessionId}`);
      
      if (res.data.status === "complete" && res.data.payment_status === "paid") {
        setStatus("success");
        // Load updated subscription
        const subRes = await apiClient.get("/agent/billing/subscription");
        setSubscription(subRes.data);
      } else if (res.data.status === "expired") {
        setStatus("error");
      } else {
        // Still processing, retry
        setTimeout(verifyPayment, 2000);
      }
    } catch (err) {
      console.error("Payment verification error", err);
      setStatus("error");
    }
  };

  if (status === "loading") {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
          <h2 className="text-xl font-semibold mb-2">Processing Payment...</h2>
          <p className="text-muted-foreground">Please wait while we confirm your subscription.</p>
        </div>
      </Layout>
    );
  }

  if (status === "error") {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Payment Failed</h2>
          <p className="text-muted-foreground mb-6 text-center max-w-md">
            We couldn't verify your payment. This could be due to a cancelled payment or 
            a temporary issue. Please try again.
          </p>
          <div className="flex gap-4">
            <Button variant="outline" onClick={() => navigate("/agent/pricing")}>
              Back to Pricing
            </Button>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="billing-success-page">
        {/* Success Icon */}
        <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-green-500" />
        </div>

        {/* Success Message */}
        <h1 className="text-2xl font-bold mb-2">Welcome to Contract Expert!</h1>
        <p className="text-muted-foreground mb-6 text-center max-w-md">
          Your subscription is now active. You have full access to all 
          {subscription?.plan_name ? ` ${subscription.plan_name}` : ""} features.
        </p>

        {/* Plan Details */}
        {subscription && (
          <div className="bg-secondary/50 rounded-xl p-6 mb-8 w-full max-w-md">
            <h3 className="font-medium mb-4">Your Plan</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plan</span>
                <span className="font-medium">{subscription.plan_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Users</span>
                <span className="font-medium">
                  {subscription.users_limit === -1 ? "Unlimited" : subscription.users_limit}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Documents</span>
                <span className="font-medium">
                  {subscription.documents_limit === -1 ? "Unlimited" : subscription.documents_limit}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium text-green-600">Active</span>
              </div>
            </div>
          </div>
        )}

        {/* Next Steps */}
        <div className="w-full max-w-md mb-8">
          <h3 className="font-medium mb-4 text-center">What's Next?</h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate("/agent/documents")}
              className="w-full flex items-center gap-4 p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors text-left"
            >
              <FileText className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <div className="font-medium">Upload Documents</div>
                <div className="text-sm text-muted-foreground">
                  Add your collective agreements
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
            </button>
            
            <button
              onClick={() => navigate("/agent/chat")}
              className="w-full flex items-center gap-4 p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors text-left"
            >
              <MessageSquare className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <div className="font-medium">Start Chatting</div>
                <div className="text-sm text-muted-foreground">
                  Ask your Contract Expert
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Main CTA */}
        <Button
          size="lg"
          onClick={() => navigate("/agent/chat")}
          className="px-8"
        >
          Go to Contract Expert
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </Layout>
  );
}
